/**
 * @fileoverview Common types for PocketBase integration
 */

// Import and re-export PocketBase native types
import type { ListResult, RecordModel } from 'pocketbase';
export type { ListResult, RecordModel } from 'pocketbase';

/**
 * Options for PocketBase queries
 */
export interface QueryOptions {
  /** Filter expression in PocketBase format */
  filter?: string;
  /** Sort expression (e.g., '-created', 'name') */
  sort?: string;
  /** Comma-separated list of relations to expand */
  expand?: string;
  /** Comma-separated list of fields to return */
  fields?: string;
}

/**
 * Configuration for repository behavior
 */
export interface RepositoryConfig {
  /** Default sort order for queries */
  defaultSort: string;
  /** Default filter for queries */
  defaultFilter: string;
  /** Default page size for paginated queries */
  defaultPageSize: number;
  /** Whether to use soft delete (set active=false) */
  enableSoftDelete: boolean;
}

/**
 * Standard response format for paginated data
 * Uses PocketBase native ListResult<T>
 */
export type PaginationResult<T> = ListResult<T>;

/**
 * Base interface for all collection records
 * Extends PocketBase RecordModel with common fields
 */
export interface BaseRecord extends RecordModel {
  /** Creation timestamp (automatically managed by PocketBase) */
  created: string;
  /** Last update timestamp (automatically managed by PocketBase) */
  updated: string;
  /** Unique record identifier (automatically managed by PocketBase) */
  id: string;
}

/**
 * Interface for records that support soft delete
 */
export interface SoftDeletableRecord extends BaseRecord {
  /** Whether the record is active (not soft-deleted) */
  activo: boolean;
}

/**
 * Generic type for create operations
 * Omits system fields that are auto-generated
 */
export type CreateData<T> = Omit<T, 'id' | 'created' | 'updated'>;

/**
 * Generic type for update operations
 * Makes all fields optional except system fields
 */
export type UpdateData<T> = Partial<Omit<T, 'id' | 'created' | 'updated'>>;

/**
 * Error types for repository operations
 */
export class RepositoryError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: any
  ) {
    super(message);
    this.name = 'RepositoryError';
  }
}

export class ValidationError extends RepositoryError {
  constructor(
    public field: string,
    message: string,
    public value?: any
  ) {
    super(`Validation error in field '${field}': ${message}`, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends RepositoryError {
  constructor(
    public resourceType: string,
    public resourceId: string
  ) {
    super(`${resourceType} with ID '${resourceId}' not found`, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class PermissionError extends RepositoryError {
  constructor(
    public operation: string,
    public resource: string
  ) {
    super(`Permission denied for ${operation} on ${resource}`, 'PERMISSION_DENIED');
    this.name = 'PermissionError';
  }
}
